import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { EmptyDataMessage } from '@/common/components/EmptyDataMessage'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Spinner from '@/common/components/Spinner'
import { useGoBack } from '@/common/hooks/useGoBack'
import { FormTransformers } from '@/common/utils/formTransformers'
import ExecutionObjectiveItem from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/components/ExecutionObjectiveItem'
import { MaintenanceDirectiveEntryExecutionHeader } from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/components/MaintenanceDirectiveEntryExecutionHeader'
import { useAdvanceMaintenanceDirectiveExecutionEntry } from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/hooks/useAdvanceMaintenanceDirectiveExecutionEntry'
import { useRegressMaintenanceDirectiveExecutionEntry } from '@/features/maintenanceWorkflow/pages/Show/features/directiveEntries/features/execution/hooks/useRegressMaintenanceDirectiveExecutionEntry'
import { useMaintenanceForwarding } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/hooks/useMaintenanceForwarding'
import {
  MaintenanceForwardingData,
  MaintenanceForwardingDirectiveEntryExecutionEntryData,
  MaintenanceForwardingDirectiveEntryStatus,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

const CompanyMaintenanceWorkflowsDirectiveEntriesExecution = () => {
  const { id: workflowId, forwardingId, directiveEntryId } = useParams()

  const goBack = useGoBack()

  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: forwarding = {} as MaintenanceForwardingData<MaintenanceForwardingDirectiveEntryExecutionEntryData>,
    isPending: forwardingIsPending,
    dataUpdatedAt: forwardingUpdatedAt,
  } = useMaintenanceForwarding(forwardingId)

  const { mutate: advance, isPending: advanceIsPending } = useAdvanceMaintenanceDirectiveExecutionEntry({
    directiveEntryId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] })

        goBack({
          fallback: `/maintenance-workflows/${workflowId}`,
          searchLastRouteFromFallback: true,
        })
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setMessages,
        })(error)
      },
    },
  })

  const { mutate: regress, isPending: regressIsPending } = useRegressMaintenanceDirectiveExecutionEntry({
    directiveEntryId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] })

        goBack({
          fallback: `/maintenance-workflows/${workflowId}`,
          searchLastRouteFromFallback: true,
        })
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setMessages,
        })(error)
      },
    },
  })

  const isInAwaitingClientReview = useMemo(() => {
    return forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.AwaitingClientReview
  }, [forwardingUpdatedAt])

  const isEveryObjectiveReviewed = useMemo(() => {
    return !forwarding?.directive_entry?.objectives?.some((objective) => !objective?.last_review?.state)
  }, [forwardingUpdatedAt])

  const isAnyObjectiveDisapproved = useMemo(() => {
    return forwarding?.directive_entry?.objectives?.some((objective) => objective?.last_review?.state === 'disapproved')
  }, [forwardingUpdatedAt])

  return (
    <>
      <div className="inner-screen">
        <MaintenanceDirectiveEntryExecutionHeader
          actions={
            <>
              {!isAnyObjectiveDisapproved && (
                <Button
                  onClick={() => advance()}
                  disabled={advanceIsPending || !isEveryObjectiveReviewed}
                  isLoading={advanceIsPending}
                  operationKlassToCheckPermission="Client::Maintenance::Directive::ExecutionEntries::Advance"
                >
                  Concluir vistoria
                </Button>
              )}

              {isAnyObjectiveDisapproved && (
                <Button
                  variant="yellow-black"
                  onClick={() => regress()}
                  disabled={regressIsPending || !isEveryObjectiveReviewed}
                  isLoading={regressIsPending}
                  operationKlassToCheckPermission="Client::Maintenance::Directive::ExecutionEntries::Regress"
                >
                  Solicitar revisão
                </Button>
              )}
            </>
          }
        />

        <Box className="flex-1 overflow-auto p-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <p className="text-base font-bold">Realize a vistoria do equipamento</p>

            <p className="text-sm">
              Marque os Objetos que foram executados de maneira adequada, caso algo não esteja da maneira esperada não
              marque, após confirmação você poderá deixar uma justificativa caso um ou mais Objetos não estejam da
              maneira esperada, o Estabelecimento será notificado e o problema deverá ser corrigido.
            </p>
          </div>

          <Box className="p-4 flex-1 flex flex-col gap-1.5">
            {Number(forwarding?.directive_entry?.objectives?.length) > 0 ? (
              forwarding?.directive_entry?.objectives?.map((objective) => (
                <ExecutionObjectiveItem
                  key={objective.id}
                  objective={objective}
                  isInAwaitingClientReview={isInAwaitingClientReview}
                />
              ))
            ) : forwardingIsPending ? (
              <div className="flex-1 flex items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <EmptyDataMessage message="Não há objetos para revisar" className="flex-1" />
            )}
          </Box>
        </Box>

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </div>
    </>
  )
}

export default CompanyMaintenanceWorkflowsDirectiveEntriesExecution
